/**
 * AI Chat Service (Frontend)
 * Handles API communication and SSE streaming for AI chat
 */

import AtriumApi from "api";

class AIChatService {
  /**
   * Send message and handle streaming response via SSE
   * @param {Array} messages - Conversation history [{role, content}]
   * @param {string} userMessage - Current message to send
   * @param {Object} callbacks - {onChunk, onComplete, onError}
   * @returns {AbortController} - Controller to abort the request
   */
  async sendMessage(messages, userMessage, { onChunk, onComplete, onError }) {
    const abortController = new AbortController();

    try {
      // Get auth token
      const token = localStorage.getItem("auth");
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Get base URL from environment or default
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

      // Get church slug
      const slug = localStorage.getItem("churchSlug") ||
                   localStorage.getItem("atriumChurchSlug") ||
                   process.env.REACT_APP_CHURCH_SLUG;

      const response = await fetch(`${baseUrl}/api/v2/ai-chat/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "text/event-stream",
          ...(slug && { "X-Church-Slug": slug }),
        },
        body: JSON.stringify({ messages, userMessage }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        await response.json().catch(() => ({}));
        throw new Error("Something went wrong. Please try again later.");
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (ending with \n\n)
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case "connected":
                  // Connection established
                  console.log("[AIChat] SSE connected");
                  break;

                case "chunk":
                  // Text chunk received
                  if (data.content && onChunk) {
                    onChunk(data.content);
                  }
                  break;

                case "complete":
                  // Response complete
                  if (onComplete) {
                    onComplete(data.artifacts || []);
                  }
                  break;

                case "error":
                  // Error from server
                  if (onError) {
                    onError(data.message || "Unknown error");
                  }
                  break;

                default:
                  console.warn("[AIChat] Unknown event type:", data.type);
              }
            } catch (parseError) {
              console.warn("[AIChat] Failed to parse SSE data:", parseError);
            }
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("[AIChat] Request aborted");
      } else {
        console.error("[AIChat] Stream error:", error);
        if (onError) {
          onError("Something went wrong. Please try again later.");
        }
      }
    }

    return abortController;
  }

  /**
   * Get contextual suggestions
   * @returns {Promise<string[]>}
   */
  async getSuggestions() {
    try {
      const response = await AtriumApi.get("/v2/ai-chat/suggestions");
      return response.data?.data?.suggestions || [];
    } catch (error) {
      console.error("[AIChat] Failed to get suggestions:", error);
      return [
        "Give me an overview of our church's current health",
        "What trends do you see in our data?",
        "Show me member demographics",
        "What ministry recommendations do you have?",
      ];
    }
  }

  /**
   * Check if AI chat is available
   * @returns {Promise<{available: boolean, model: string|null}>}
   */
  async getStatus() {
    try {
      const response = await AtriumApi.get("/v2/ai-chat/status");
      return response.data?.data || { available: false, model: null };
    } catch (error) {
      console.error("[AIChat] Failed to get status:", error);
      return { available: false, model: null };
    }
  }

  /**
   * Get organization context (for debugging)
   * @returns {Promise<Object>}
   */
  async getContext() {
    try {
      const response = await AtriumApi.get("/v2/ai-chat/context");
      return response.data?.data || null;
    } catch (error) {
      console.error("[AIChat] Failed to get context:", error);
      return null;
    }
  }
}

export default new AIChatService();
